import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import { useState, useEffect, useMemo, useCallback } from "react";
import * as FaIcons from "react-icons/fa";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import { Link } from "react-router-dom";
import moment from "moment-timezone";
import Swal from "sweetalert2";
import { AttendanceStatusBadge } from "./attendanceStatusBadge";
import {
  ATTENDANCE_KPI_LABELS,
  matchesAttendanceKpiFilter,
} from "./attendanceStatusFilter";
import {
  ATT_TABLE_CELL,
  ATT_TABLE_HEAD,
  ATT_TABLE_HEAD_CENTER,
  ATT_TABLE_ROW,
  AttendanceDateChip,
  AttendanceDateInput,
  AttendanceEmployeeCell,
  AttendanceEmpty,
  AttendanceFilterChip,
  AttendanceFilterField,
  AttendanceFilterQuickGroup,
  AttendanceFilterRow,
  AttendanceFiltersPanel,
  AttendanceHoursBadge,
  AttendanceKpiCard,
  AttendanceKpiGrid,
  AttendancePageHeader,
  AttendancePageShell,
  AttendancePermissionDenied,
  AttendanceSearchInput,
  AttendanceSelect,
  AttendanceTableWrap,
  AttendanceTimePill,
} from "./attendanceUiShared";
import "../../styles/tailwind.css";

const KPI_ITEMS = [
  { key: "total_users", label: "Employees", icon: FaIcons.FaUsers, tone: "primary" },
  { key: "on_time", label: "On time", icon: FaIcons.FaCheckCircle, tone: "success" },
  { key: "late", label: "Late", icon: FaIcons.FaClock, tone: "warning" },
  { key: "absent", label: "Absent", icon: FaIcons.FaUserTimes, tone: "danger" },
  { key: "holiday", label: "Holiday", icon: FaIcons.FaUmbrellaBeach, tone: "info" },
  { key: "weekend", label: "Weekend", icon: FaIcons.FaCalendarDay, tone: "info" },
];

function formatCheckTime(value, timezone) {
  if (!value) return null;
  return moment.utc(value).tz(timezone).format("HH:mm");
}

function toDateString(d) {
  return d.toISOString().split("T")[0];
}

export default function EmployeeAttendance({ embedded = true }) {
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState([]);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [summary, setSummary] = useState({});
  const [permissions, setPermissions] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);

  const today = toDateString(new Date());
  const [filters, setFilters] = useState({
    date: today,
    branch_id: "all",
    search: "",
  });
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [editingUserId, setEditingUserId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const actionBtnClass =
    "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-[#007BFF] shadow-sm transition hover:bg-gray-50 disabled:opacity-50";
  const actionBtnSaveClass =
    "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm transition hover:bg-emerald-100 disabled:opacity-50";

  const extractTimeForEdit = (datetimeStr, timezone) => {
    if (!datetimeStr) return "";
    return moment.utc(datetimeStr).tz(timezone || "UTC").format("HH:mm");
  };

  const updateEditForm = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditRow = (record) => {
    const tz = record.userBranchTimeZone || "UTC";
    setEditingUserId(record.id);
    setEditForm({
      branch_id: record.branch_id || "",
      user_id: record.id,
      check_in: extractTimeForEdit(record.check_in, tz),
      check_out: extractTimeForEdit(record.check_out, tz),
    });
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditForm(null);
  };

  const handleSaveRow = async () => {
    if (!editForm?.user_id || !filters.date) return;
    try {
      setSavingEdit(true);
      const response = await apiUrl.post("/edit_user_attendance", {
        branch_id: editForm.branch_id || "",
        user_id: editForm.user_id,
        check_in: editForm.check_in || "",
        check_out: editForm.check_out || "",
        date: filters.date,
      });
      if (response?.status) {
        Swal.fire({
          title: response?.data?.message || "Attendance updated",
          icon: "success",
          timer: 2500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        handleCancelEdit();
        dailyUserAttendance();
      } else {
        Swal.fire({
          title: response?.data?.message || "Could not save attendance",
          icon: "error",
          toast: true,
          position: "top-end",
          timer: 3000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.data?.message ||
        "Could not save attendance";
      Swal.fire({
        title: msg,
        icon: "error",
        toast: true,
        position: "top-end",
        timer: 3000,
        showConfirmButton: false,
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const selectedDateLabel = useMemo(() => {
    if (!filters.date) return "";
    return moment(filters.date).format("dddd, D MMMM YYYY");
  }, [filters.date]);

  const isToday = filters.date === today;
  const yesterday = toDateString(new Date(Date.now() - 86400000));
  const isYesterday = filters.date === yesterday;

  const displayData = useMemo(
    () => data.filter((row) => matchesAttendanceKpiFilter(row, statusFilter)),
    [data, statusFilter],
  );

  const rosterTitle = useMemo(() => {
    if (!statusFilter || statusFilter === "total_users") return "Employee roster";
    return ATTENDANCE_KPI_LABELS[statusFilter] || "Employee roster";
  }, [statusFilter]);

  const branchLabel = useMemo(() => {
    if (filters.branch_id === "all") return null;
    const b = branches.find((br) => String(br.id) === String(filters.branch_id));
    return b?.name || "Branch";
  }, [filters.branch_id, branches]);

  const filterChips = useMemo(() => {
    const chips = [];
    if (statusFilter && statusFilter !== "total_users") {
      chips.push({
        id: "status",
        label: `Status: ${ATTENDANCE_KPI_LABELS[statusFilter]}`,
        onRemove: () => setStatusFilter(null),
      });
    }
    if (filters.date !== today) {
      chips.push({
        id: "date",
        label: `Date: ${moment(filters.date).format("D MMM YYYY")}`,
        onRemove: () => setFilters((f) => ({ ...f, date: today })),
      });
    }
    if (branchLabel) {
      chips.push({
        id: "branch",
        label: `Branch: ${branchLabel}`,
        onRemove: () => setFilters((f) => ({ ...f, branch_id: "all" })),
      });
    }
    if (filters.search.trim()) {
      chips.push({
        id: "search",
        label: `Search: ${filters.search.trim()}`,
        onRemove: () => setFilters((f) => ({ ...f, search: "" })),
      });
    }
    return chips;
  }, [statusFilter, filters.date, filters.search, branchLabel, today]);

  const dailyUserAttendance = useCallback(async () => {
    try {
      setShowSkeleton(true);
      const response = await apiUrl.get("/emp_attendance_daily", {
        params: {
          date: filters.date,
          branch_id: filters.branch_id,
          search: debouncedSearch,
        },
      });
      if (response?.status) {
        setData(response?.data?.data ?? []);
        setSummary(response?.data?.summary ?? {});
      }
    } catch (error) {
      console.log(error);
    } finally {
      setShowSkeleton(false);
    }
  }, [filters.date, filters.branch_id, debouncedSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search.trim()), 350);
    return () => clearTimeout(t);
  }, [filters.search]);

  useEffect(() => {
    apiUrl
      .get("/getBranches")
      .then((response) => {
        if (response?.status) setBranches(response?.data?.data ?? []);
      })
      .catch(console.log);
  }, []);

  useEffect(() => {
    const perms = getPermissions();
    if (perms) setPermissions(perms);
  }, []);

  useEffect(() => {
    if (permissions?.manage_policies === 1) {
      dailyUserAttendance();
    }
  }, [dailyUserAttendance, permissions]);

  useEffect(() => {
    setEditingUserId(null);
    setEditForm(null);
  }, [filters.date]);

  const clearAllFilters = () => {
    setStatusFilter(null);
    setFilters({ date: today, branch_id: "all", search: "" });
  };

  const handleKpiClick = (key) => {
    if (key === "total_users") {
      setStatusFilter(null);
      return;
    }
    setStatusFilter((prev) => (prev === key ? null : key));
  };

  if (permissions === null) {
    return (
      <AttendancePageShell embedded={embedded}>
        <div className="flex min-h-[200px] items-center justify-center text-sm text-gray-500">
          Loading…
        </div>
      </AttendancePageShell>
    );
  }

  if (permissions.manage_policies !== 1) {
    return (
      <AttendancePageShell embedded={embedded}>
        <AttendancePermissionDenied />
      </AttendancePageShell>
    );
  }

  const hasRows = !showSkeleton && displayData.length > 0;
  const hasDataButFilteredEmpty =
    !showSkeleton && data.length > 0 && displayData.length === 0 && statusFilter;

  const rosterCountLabel = showSkeleton ? (
    <span className="text-xs text-gray-400">Loading…</span>
  ) : (
    <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-semibold text-gray-600">
      {displayData.length}
      {statusFilter && statusFilter !== "total_users" ? ` of ${data.length}` : ""} shown
    </span>
  );

  const rosterTableHead = (
    <thead>
      <tr>
        <th className={ATT_TABLE_HEAD}>Employee</th>
        <th className={ATT_TABLE_HEAD_CENTER}>Check in</th>
        <th className={ATT_TABLE_HEAD_CENTER}>Check out</th>
        <th className={ATT_TABLE_HEAD_CENTER}>Hours</th>
        <th className={ATT_TABLE_HEAD}>Status</th>
        <th className={ATT_TABLE_HEAD}>Notes</th>
        <th className={ATT_TABLE_HEAD_CENTER}>Edit</th>
      </tr>
    </thead>
  );

  let rosterBody = null;
  if (showSkeleton) {
    rosterBody = (
      <AttendanceTableWrap>
        {rosterTableHead}
        <tbody>
          {Array.from({ length: 8 }).map((_, i) => (
            <tr key={i} className={ATT_TABLE_ROW}>
              {Array.from({ length: 7 }).map((__, j) => (
                <td key={j} className={ATT_TABLE_CELL}>
                  <div className="h-4 animate-pulse rounded bg-gray-200/80" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </AttendanceTableWrap>
    );
  } else if (hasRows) {
    rosterBody = (
      <AttendanceTableWrap>
        {rosterTableHead}
        <tbody>
          {displayData.map((record) => {
            const tz = record.userBranchTimeZone || "UTC";
            const isEditing = editingUserId === record.id;
            const checkIn = formatCheckTime(record.check_in, tz);
            const checkOut = formatCheckTime(record.check_out, tz);
            return (
              <tr key={record.id} className={ATT_TABLE_ROW}>
                <td className={ATT_TABLE_CELL}>
                  <AttendanceEmployeeCell
                    name={record.name}
                    employeeId={record.id}
                    branchName={record.branch_name}
                  />
                </td>
                <td className={`${ATT_TABLE_CELL} text-center`}>
                  {isEditing ? (
                    <input
                      type="time"
                      value={editForm?.check_in || ""}
                      onChange={(e) => updateEditForm("check_in", e.target.value)}
                      className="mx-auto block h-8 w-[7.5rem] rounded-md border border-gray-300 px-2 text-[12px]"
                      step="60"
                    />
                  ) : (
                    <AttendanceTimePill time={checkIn} variant="in" />
                  )}
                </td>
                <td className={`${ATT_TABLE_CELL} text-center`}>
                  {isEditing ? (
                    <input
                      type="time"
                      value={editForm?.check_out || ""}
                      onChange={(e) => updateEditForm("check_out", e.target.value)}
                      className="mx-auto block h-8 w-[7.5rem] rounded-md border border-gray-300 px-2 text-[12px]"
                      step="60"
                    />
                  ) : (
                    <AttendanceTimePill time={checkOut} variant="out" />
                  )}
                </td>
                <td className={`${ATT_TABLE_CELL} text-center`}>
                  <AttendanceHoursBadge value={record.working_hours_formatted} />
                </td>
                <td className={ATT_TABLE_CELL}>
                  <AttendanceStatusBadge status={record.attendance_status} />
                </td>
                <td className={ATT_TABLE_CELL}>
                  {record.attendance_notes ? (
                    <p className="m-0 max-w-[240px] text-[12px] leading-snug text-gray-600">
                      {record.attendance_notes}
                    </p>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className={`${ATT_TABLE_CELL} text-center`}>
                  <div className="flex justify-center gap-1">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          className={actionBtnSaveClass}
                          onClick={handleSaveRow}
                          disabled={savingEdit}
                          title="Save"
                        >
                          <FaIcons.FaSave size={14} />
                        </button>
                        <button
                          type="button"
                          className={actionBtnClass}
                          onClick={handleCancelEdit}
                          disabled={savingEdit}
                          title="Cancel"
                        >
                          <FaIcons.FaTimes size={14} />
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className={actionBtnClass}
                        onClick={() => handleEditRow(record)}
                        title="Edit check-in / check-out"
                      >
                        <FaIcons.FaEdit size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </AttendanceTableWrap>
    );
  } else {
    rosterBody = (
      <AttendanceEmpty
        icon={hasDataButFilteredEmpty ? FaIcons.FaFilter : FaIcons.FaUserClock}
        title={
          hasDataButFilteredEmpty
            ? `No ${ATTENDANCE_KPI_LABELS[statusFilter]?.toLowerCase() || ""} employees`
            : "No one matches these filters"
        }
        description={
          hasDataButFilteredEmpty
            ? "Try another summary card or clear the status filter."
            : "Try Today, another branch, or clear your search."
        }
      />
    );
  }

  return (
    <AttendancePageShell embedded={embedded}>
      {!embedded ? (
        <AttendancePageHeader
          icon={FaIcons.FaClipboardCheck}
          title="Employee attendance"
          subtitle="Click a summary card to filter the roster. Change date, branch, or search below."
          actions={
            <Link
              to="/attendence/report"
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-4 text-[13px] font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <FaIcons.FaChartBar className="text-emerald-600" />
              Reports
            </Link>
          }
        />
      ) : (
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="m-0 text-[13px] font-bold text-[#35314d]">{selectedDateLabel}</p>
          <Link to="/attendence/report" className="text-[11px] font-semibold text-[#007BFF] hover:underline">
            Reports
          </Link>
        </div>
      )}

      <AttendanceKpiGrid>
        {KPI_ITEMS.map(({ key, label, icon, tone }) => {
          const active = key === "total_users" ? !statusFilter : statusFilter === key;
          return (
            <AttendanceKpiCard
              key={key}
              label={label}
              icon={icon}
              tone={tone}
              loading={showSkeleton}
              value={summary?.[key] ?? 0}
              active={active}
              onClick={() => handleKpiClick(key)}
            />
          );
        })}
      </AttendanceKpiGrid>

      <AttendanceFiltersPanel
        compact
        showClearAll={filterChips.length > 0}
        onClearAll={clearAllFilters}
        chips={filterChips.map((chip) => (
          <AttendanceFilterChip key={chip.id} label={chip.label} onRemove={chip.onRemove} />
        ))}
      >
        <AttendanceFilterRow template="auto 8.75rem minmax(7.5rem, 1.1fr) minmax(10rem, 2fr)">
          <AttendanceFilterQuickGroup>
            <AttendanceDateChip active={isToday} onClick={() => setFilters((f) => ({ ...f, date: today }))}>
              Today
            </AttendanceDateChip>
            <AttendanceDateChip
              active={isYesterday}
              onClick={() => setFilters((f) => ({ ...f, date: yesterday }))}
            >
              Yesterday
            </AttendanceDateChip>
          </AttendanceFilterQuickGroup>
          <AttendanceFilterField label="Date">
            <AttendanceDateInput
              id="attendance-date"
              narrow
              value={filters.date}
              onChange={(e) => setFilters((f) => ({ ...f, date: e.target.value }))}
            />
          </AttendanceFilterField>
          <AttendanceFilterField label="Branch">
            <AttendanceSelect
              id="attendance-branch"
              value={filters.branch_id}
              onChange={(e) => setFilters((f) => ({ ...f, branch_id: e.target.value }))}
            >
              <option value="all">All branches</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>{branch.name}</option>
              ))}
            </AttendanceSelect>
          </AttendanceFilterField>
          <AttendanceFilterField label="Search">
            <AttendanceSearchInput
              id="attendance-search"
              placeholder="Employee name…"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
          </AttendanceFilterField>
        </AttendanceFilterRow>
      </AttendanceFiltersPanel>

      <section className="mt-3" aria-labelledby="employee-roster-heading">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2
            id="employee-roster-heading"
            className="m-0 text-[13px] font-bold text-[#35314d]"
          >
            {rosterTitle}
          </h2>
          {rosterCountLabel}
        </div>
        <div className="w-full overflow-x-auto bg-white">{rosterBody}</div>
      </section>
    </AttendancePageShell>
  );
}
